import React, { useState } from 'react';
import { Building2 } from 'lucide-react';
import CardsResumo from './resumo/CardsResumo';
import TabelaContasBancarias from './tabela/TabelaContasBancarias';
import GraficoValores from './Graficos/GraficoValores';
import GraficoStatus from './Graficos/GraficoStatus';
import GraficoTipos from './Graficos/GraficoTipos';
import ModalListaContas from './modal/ModalListaContas';

const AnaliseContaBancaria = ({ contas }) => {
  const [visualizacaoAtiva, setVisualizacaoAtiva] = useState('resumo');
  const [modalAberto, setModalAberto] = useState(false);
  const [contasModal, setContasModal] = useState([]);
  const [tituloModal, setTituloModal] = useState('');
  
  // Processar dados
  const dadosContaBancaria = React.useMemo(() => {
    if (!contas || contas.length === 0) return [];
    
    const dadosPorConta = {};
    
    contas.forEach(conta => {
      const contaBancaria = conta.conta || 'Não informado';
      
      if (!dadosPorConta[contaBancaria]) {
        dadosPorConta[contaBancaria] = {
          nome: contaBancaria,
          totalContas: 0,
          valorTotal: 0,
          valorPago: 0,
          valorAberto: 0,
          contasPorStatus: {
            paga: 0,
            paga_parcialmente: 0,
            vencida: 0,
            proxima_vencimento: 0,
            hoje: 0,
            proximos_meses: 0,
            cancelada: 0
          },
          contasPorTipoPagamento: {},
          contasPorPrioridade: {
            alta: 0,
            media: 0,
            baixa: 0
          },
          fornecedoresUnicos: new Set(),
          categoriasUnicas: new Set(),
          contasVencidas: 0,
          valorVencido: 0,
          proximasVencimento: 0,
          valorProximasVencimento: 0
        };
      }
      
      const dados = dadosPorConta[contaBancaria];
      
      // Contadores básicos
      dados.totalContas++;
      dados.valorTotal += conta.valor || 0;
      dados.valorPago += conta.valor_pago || 0;
      dados.valorAberto += conta.valor_aberto || 0;
      
      // Status das contas
      if (dados.contasPorStatus.hasOwnProperty(conta.status)) {
        dados.contasPorStatus[conta.status]++;
      }
      
      // Tipo de pagamento - normalizar para garantir consistência
      const tipoPagamentoOriginal = conta.tipo_pagamento || 'Não informado';
      // Normalizar tipos de pagamento comuns (case-insensitive)
      const tipoPagamentoNormalizado = (() => {
        const tipoLower = tipoPagamentoOriginal.toLowerCase().trim();
        if (tipoLower === 'pix' || tipoLower === 'pix ') {
          return 'PIX';
        } else if (tipoLower === 'boleto' || tipoLower === 'bolet') {
          return 'Boleto';
        } else if (tipoLower.includes('transferência') || tipoLower.includes('transferencia')) {
          return 'Transferência';
        } else if (tipoLower === 'dinheiro') {
          return 'Dinheiro';
        } else if (tipoLower === 'cheque') {
          return 'Cheque';
        } else if (tipoLower.includes('cartão') || tipoLower.includes('cartao')) {
          return 'Cartão';
        } else if (tipoLower.includes('depósito') || tipoLower.includes('deposito')) {
          return 'Depósito';
        } else if (tipoLower.includes('débito em conta') || tipoLower.includes('debito em conta')) {
          return 'Débito em conta';
        }
        // Se não for um dos tipos conhecidos, manter o original mas capitalizar primeira letra
        return tipoPagamentoOriginal.charAt(0).toUpperCase() + tipoPagamentoOriginal.slice(1).toLowerCase();
      })();
      
      if (!dados.contasPorTipoPagamento[tipoPagamentoNormalizado]) {
        dados.contasPorTipoPagamento[tipoPagamentoNormalizado] = 0;
      }
      dados.contasPorTipoPagamento[tipoPagamentoNormalizado]++;
      
      // Prioridade
      if (dados.contasPorPrioridade.hasOwnProperty(conta.prioridade)) {
        dados.contasPorPrioridade[conta.prioridade]++;
      }
      
      // Fornecedores únicos
      dados.fornecedoresUnicos.add(conta.fornecedor);
      
      // Categorias únicas
      dados.categoriasUnicas.add(conta.categoria);
      
      // Contas vencidas e próximas ao vencimento
      if (conta.status === 'vencida') {
        dados.contasVencidas++;
        dados.valorVencido += conta.valor || 0;
      } else if (conta.status === 'proxima_vencimento' || conta.status === 'hoje') {
        dados.proximasVencimento++;
        dados.valorProximasVencimento += conta.valor || 0;
      }
    });
    
    // Converter Sets para arrays e calcular percentuais
    const resultado = Object.values(dadosPorConta).map(dados => ({
      ...dados,
      fornecedoresUnicos: dados.fornecedoresUnicos.size,
      categoriasUnicas: dados.categoriasUnicas.size,
      percentualPago: dados.valorTotal > 0 ? (dados.valorPago / dados.valorTotal) * 100 : 0,
      percentualAberto: dados.valorTotal > 0 ? (dados.valorAberto / dados.valorTotal) * 100 : 0,
      percentualVencido: dados.valorTotal > 0 ? (dados.valorVencido / dados.valorTotal) * 100 : 0,
      percentualProximasVencimento: dados.valorTotal > 0 ? (dados.valorProximasVencimento / dados.valorTotal) * 100 : 0
    }));
    
    // Ordenar por valor total decrescente
    return resultado.sort((a, b) => b.valorTotal - a.valorTotal);
  }, [contas]);

  // Dados para gráficos
  const dadosGraficoValores = dadosContaBancaria.map(conta => ({
    nome: conta.nome.length > 15 ? conta.nome.substring(0, 15) + '...' : conta.nome,
    nomeCompleto: conta.nome,
    valorTotal: conta.valorTotal,
    valorAberto: conta.valorAberto,
    valorPago: conta.valorPago,
    totalContas: conta.totalContas,
    contasVencidas: conta.contasVencidas,
    valorVencido: conta.valorVencido,
    proximasVencimento: conta.proximasVencimento,
    valorProximasVencimento: conta.valorProximasVencimento
  }));

  const dadosGraficoStatus = dadosContaBancaria.map(conta => ({
    nome: conta.nome.length > 15 ? conta.nome.substring(0, 15) + '...' : conta.nome,
    nomeCompleto: conta.nome,
    vencidas: conta.contasVencidas,
    proximasVencimento: conta.proximasVencimento,
    hoje: conta.contasPorStatus.hoje,
    pagas: conta.contasPorStatus.paga,
    pagasParcialmente: conta.contasPorStatus.paga_parcialmente,
    emAberto: conta.contasPorStatus.proximos_meses,
    canceladas: conta.contasPorStatus.cancelada,
    totalContas: conta.totalContas
  }));

  // Tipos de pagamento
  const tiposPagamentoMap = {
    'Boleto': { dataKey: 'boleto', nome: 'Boleto', cor: '#3B82F6' },
    'Transferência': { dataKey: 'transferencia', nome: 'Transferência', cor: '#10B981' },
    'PIX': { dataKey: 'pix', nome: 'PIX', cor: '#8B5CF6' },
    'Dinheiro': { dataKey: 'dinheiro', nome: 'Dinheiro', cor: '#F59E0B' },
    'Cheque': { dataKey: 'cheque', nome: 'Cheque', cor: '#EF4444' },
    'Cartão': { dataKey: 'cartao', nome: 'Cartão', cor: '#06B6D4' },
    'Depósito': { dataKey: 'deposito', nome: 'Depósito', cor: '#84CC16' },
    'Débito em conta': { dataKey: 'debitoConta', nome: 'Débito em conta', cor: '#F97316' }
  };
  
  const dadosGraficoTipoPagamento = dadosContaBancaria.map(conta => {
    const dados = {
      nome: conta.nome.length > 15 ? conta.nome.substring(0, 15) + '...' : conta.nome,
      nomeCompleto: conta.nome,
      totalContas: conta.totalContas || 0
    };
    
    Object.values(tiposPagamentoMap).forEach(tipo => {
      dados[tipo.dataKey] = 0;
    });
    
    Object.entries(conta.contasPorTipoPagamento).forEach(([tipo, qtd]) => {
      if (tiposPagamentoMap[tipo]) {
        dados[tiposPagamentoMap[tipo].dataKey] = qtd || 0;
      }
    });
    
    const outros = Object.entries(conta.contasPorTipoPagamento)
      .filter(([tipo]) => !tiposPagamentoMap[tipo])
      .reduce((sum, [, qtd]) => sum + (qtd || 0), 0);
    
    dados.outros = outros;
    
    if (!dados.totalContas || dados.totalContas === 0) {
      dados.totalContas = Object.values(tiposPagamentoMap).reduce(
        (sum, tipo) => sum + dados[tipo.dataKey], 0
      ) + outros;
    }
    
    return dados;
  });
  
  const tiposComContas = Object.entries(tiposPagamentoMap).filter(([, config]) => {
    return dadosGraficoTipoPagamento.some(conta => conta[config.dataKey] > 0);
  }).map(([tipo, config]) => ({ tipo, ...config }));
  
  const temOutros = dadosGraficoTipoPagamento.some(conta => conta.outros > 0);
  if (temOutros) {
    tiposComContas.push({ 
      tipo: 'Outros', 
      dataKey: 'outros', 
      nome: 'Outros', 
      cor: '#6B7280' 
    });
  }

  // Handlers para cliques
  const handleTotalContasClick = (contaBancaria) => {
    // Filtrar todas as contas desta conta bancária
    const contasFiltradas = contas.filter(c => {
      const contaBancariaConta = c.conta || 'Não informado';
      return contaBancariaConta === contaBancaria.nome;
    });
    
    setContasModal(contasFiltradas);
    setTituloModal(`Todas as Contas - ${contaBancaria.nome}`);
    setModalAberto(true);
  };

  const handleVencidasClick = (contaBancaria) => {
    // Filtrar apenas contas vencidas desta conta bancária
    const contasFiltradas = contas.filter(c => {
      const contaBancariaConta = c.conta || 'Não informado';
      if (contaBancariaConta !== contaBancaria.nome) return false;
      
      // Verificar se está vencida
      if (c.status === 'vencida') return true;
      
      // Verificar data de vencimento como fallback
      const dataVencimento = c.data_vencimento_convertida || c.dataVencimento;
      if (!dataVencimento) return false;
      
      const vencimento = new Date(dataVencimento);
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      vencimento.setHours(0, 0, 0, 0);
      
      return vencimento < hoje;
    });
    
    setContasModal(contasFiltradas);
    setTituloModal(`Contas Vencidas - ${contaBancaria.nome}`);
    setModalAberto(true);
  };

  const handleProximasClick = (contaBancaria) => {
    // Filtrar apenas contas próximas ao vencimento desta conta bancária
    const contasFiltradas = contas.filter(c => {
      const contaBancariaConta = c.conta || 'Não informado';
      if (contaBancariaConta !== contaBancaria.nome) return false;
      
      // Verificar se está próximo ao vencimento pelo status
      if (c.status === 'proxima_vencimento' || c.status === 'hoje') return true;
      
      // Verificar data de vencimento como fallback (próximos 7 dias)
      const dataVencimento = c.data_vencimento_convertida || c.dataVencimento;
      if (!dataVencimento) return false;
      
      const vencimento = new Date(dataVencimento);
      const hoje = new Date();
      const proximos7Dias = new Date();
      proximos7Dias.setDate(hoje.getDate() + 7);
      
      hoje.setHours(0, 0, 0, 0);
      proximos7Dias.setHours(23, 59, 59, 999);
      vencimento.setHours(0, 0, 0, 0);
      
      return vencimento >= hoje && vencimento <= proximos7Dias;
    });
    
    setContasModal(contasFiltradas);
    setTituloModal(`Contas Próximas ao Vencimento - ${contaBancaria.nome}`);
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setContasModal([]);
    setTituloModal('');
  };

  const tabs = [
    { id: 'resumo', label: 'Resumo' },
    { id: 'valores', label: 'Valores' },
    { id: 'status', label: 'Status' },
    { id: 'tipos', label: 'Tipos' }
  ];

  if (!contas || contas.length === 0) {
    return (
      <div className="bg-[color:var(--surface)] rounded-xl p-6 shadow-sm border border-[color:var(--border)]">
        <div className="text-center text-[color:var(--text-muted)]">
          <Building2 className="h-12 w-12 mx-auto mb-4 text-[color:var(--text-muted)]" />
          <p>Nenhuma conta encontrada para análise</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[color:var(--surface)] rounded-xl p-6 shadow-sm border border-[color:var(--border)]">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Building2 className="h-6 w-6 text-[color:var(--accent)]" />
          <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">Análise por Conta Bancária</h3>
        </div>

        {/* Desktop: pills */}
        <div role="tablist" aria-label="Visualizações da análise bancária" className="hidden sm:flex flex-wrap gap-2 rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-1">
          {tabs.map((item) => (
            <button
              key={item.id}
              id={`tab-analise-${item.id}`}
              role="tab"
              aria-selected={visualizacaoAtiva === item.id}
              aria-controls={`tabpanel-analise-${item.id}`}
              tabIndex={visualizacaoAtiva === item.id ? 0 : -1}
              onClick={() => setVisualizacaoAtiva(item.id)}
              className={`px-3 py-1.5 text-sm rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)] ${
                visualizacaoAtiva === item.id
                  ? 'bg-[color:var(--accent)] text-white'
                  : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* Mobile: grid 2x2 */}
        <div role="tablist" aria-label="Visualizações da análise bancária" className="sm:hidden grid grid-cols-2 gap-2">
          {tabs.map((item) => (
            <button
              key={item.id}
              id={`tab-analise-mobile-${item.id}`}
              role="tab"
              aria-selected={visualizacaoAtiva === item.id}
              aria-controls={`tabpanel-analise-${item.id}`}
              tabIndex={visualizacaoAtiva === item.id ? 0 : -1}
              onClick={() => setVisualizacaoAtiva(item.id)}
              className={`px-3 py-2 text-xs rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)] ${
                visualizacaoAtiva === item.id
                  ? 'bg-[color:var(--accent)] text-white border-[color:var(--accent)]'
                  : 'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] border-[color:var(--border)]'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {visualizacaoAtiva === 'resumo' && (
        <div id="tabpanel-analise-resumo" role="tabpanel" aria-labelledby="tab-analise-resumo" className="space-y-6">
          <CardsResumo dadosContaBancaria={dadosContaBancaria} />
          <TabelaContasBancarias 
            dadosContaBancaria={dadosContaBancaria}
            onTotalContasClick={handleTotalContasClick}
            onVencidasClick={handleVencidasClick}
            onProximasClick={handleProximasClick}
          />
        </div>
      )}

      {visualizacaoAtiva === 'valores' && (
        <div id="tabpanel-analise-valores" role="tabpanel" aria-labelledby="tab-analise-valores">
          <GraficoValores dadosGraficoValores={dadosGraficoValores} />
        </div>
      )}

      {visualizacaoAtiva === 'status' && (
        <div id="tabpanel-analise-status" role="tabpanel" aria-labelledby="tab-analise-status">
          <GraficoStatus dadosGraficoStatus={dadosGraficoStatus} />
        </div>
      )}

      {visualizacaoAtiva === 'tipos' && (
        <div id="tabpanel-analise-tipos" role="tabpanel" aria-labelledby="tab-analise-tipos">
          <GraficoTipos 
            dadosGraficoTipoPagamento={dadosGraficoTipoPagamento} 
            tiposComContas={tiposComContas}
          />
        </div>
      )}

      <ModalListaContas
        isOpen={modalAberto}
        onClose={fecharModal}
        contas={contasModal}
        titulo={tituloModal}
      />
    </div>
  );
};

export default AnaliseContaBancaria;


